/* eslint-disable react-hooks/exhaustive-deps */
import { faArrowUpRightFromSquare, faGearCode, faStar, faUserShield, faWindowRestore } from '@fortawesome/pro-light-svg-icons';
import { faStar as faStarSolid } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Backdrop, Box, Chip, IconButton, Paper, Typography } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import React, { useEffect, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router';
import { useAllowedActions } from '../../authentication';
import { useFeatureToggles } from '../../feature-toggles';
import { useTenantAddons } from '../tenant-addons';
import omnibarEntries from './entries';
import { OmnibarEntry } from './omnibar-interfaces';
import entryAccessFilter from './utils/entry-access-filter';
import entryContextFilter from './utils/entry-context-filter';
import entryAuthorityCitizenFilter from './utils/entry-authority-citizen-filter';
import { currentContextFilterLabel, matchesContextFilter, OmnibarContextFilter } from './utils/context-filter';
import { OmnibarContextFilters } from './omnibar-context-filters';
import { OmnibarEmptyResults } from './omnibar-empty-results';
import { openRankedSearch } from '../../areas/ranked-search/components/search-drawer/search-drawer-container';
import { useRoleThemes } from '../../authentication/use-role-themes';
import entrySort from './utils/entry-sort';
import { relevanceFilterOptions } from './utils/filter-options';
import useOmnibarContext from './utils/use-omnibar-context';
import { applyCatalogCorrections, catalogCorrectionsToggle } from './utils/catalog-corrections';
import { entryActionType } from './utils/entry-action-type';
import { entryOpensIn, OmnibarOpensIn } from './utils/entry-opens-in';
import { OmnibarKeyboardHelp } from './omnibar-keyboard-help';
import { updateSelectionCount, getTopSelectedResults } from './utils/top-entries';
import { composeStartEntries, recordRecentSelection } from './utils/start-entries';
import { getFavoriteIds, toggleFavorite } from './utils/favorite-entries';
import { useOmnibar } from './omnibar-provider';
import { DynamicLinks } from '../dynamic-links-modal/dynamic-links-modal';
import { useTenantConfiguration } from '../../hooks/use-tenant-configuration';
import { useServiceProviderAreaAccess } from '../../areas/citizen/serviceprovider/hooks/use-service-provider-area-access';
import { OmnibarActiveContext } from './omnibar-active-context';
import { isRecordIdentifierEntry, recordIdentifierEntries } from './entries/create-records';
import { adminMenuEntries, OmnibarAdminLinks } from './omnibar-admin-links';
import { IAdminMenuLink } from '../../areas/admin/components/admin-area-menu-links';

const OmnibarTopResultThreshold = 2;

function RestrictedIcon({ title = 'Kræver rettigheder, som du muligvis ikke har' }: { title?: string }) {
  return (
    <Box sx={{ marginLeft: 'auto', color: 'rgba(0,0,0,.5)' }} title={title}>
      <FontAwesomeIcon icon={faUserShield} />
    </Box>
  );
}

function CondtionallyEnabledIcon({ title = 'Funktionalitet afhængig af omstændigheder der muligvis ikke er opfyldt' }: { title?: string }) {
  return (
    <Box sx={{ marginLeft: 'auto', color: 'rgba(0,0,0,.5)' }} title={title}>
      <FontAwesomeIcon icon={faGearCode} />
    </Box>
  );
}

function renderOption(props: React.HTMLAttributes<HTMLLIElement>, option: OmnibarEntry) {
  return (
    <li {...props}>
      {option.label}
      {option.isPossiblyRestricted && <RestrictedIcon />}
      {option.isConditionallyEnabled && <CondtionallyEnabledIcon />}
    </li>
  );
}

function FavoriteButton({ isFavorite, onToggle }: { isFavorite: boolean; onToggle: () => void }) {
  const label = isFavorite ? 'Fjern som favorit' : 'Fastgør som favorit';
  return (
    <IconButton
      size="small"
      aria-label={label}
      title={label}
      sx={{ ml: 1 }}
      // Keeps the click from reaching the option, which would select it and close the omnibar.
      onMouseDown={event => event.preventDefault()}
      onClick={event => {
        event.stopPropagation();
        onToggle();
      }}
    >
      <FontAwesomeIcon icon={isFavorite ? faStarSolid : faStar} />
    </IconButton>
  );
}

function OpensInIcon({ opensIn }: { opensIn: OmnibarOpensIn }) {
  const title = opensIn === 'dialog' ? 'Åbner i en dialog' : 'Åbner i nyt vindue';
  return (
    <Box sx={{ color: 'rgba(0,0,0,.5)' }} title={title} aria-label={title} role="img">
      <FontAwesomeIcon icon={opensIn === 'dialog' ? faWindowRestore : faArrowUpRightFromSquare} />
    </Box>
  );
}

function renderOptionWithActionType(props: React.HTMLAttributes<HTMLLIElement>, option: OmnibarEntry, favoriteIds: string[], onToggleFavorite: (id: string) => void) {
  const opensIn = entryOpensIn(option);
  return (
    <li {...props}>
      <Chip label={entryActionType(option)} size="small" variant="outlined" sx={{ mr: 1, minWidth: 64 }} />
      <Box>
        {option.label}
        {option.availabilityHint && (
          <Typography variant="caption" component="div" color="text.secondary">
            {option.availabilityHint}
          </Typography>
        )}
      </Box>
      <Box sx={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
        {option.isPossiblyRestricted && <RestrictedIcon title={option.availabilityHint} />}
        {option.isConditionallyEnabled && <CondtionallyEnabledIcon title={option.availabilityHint} />}
        {opensIn !== 'sameTab' && <OpensInIcon opensIn={opensIn} />}
        <FavoriteButton isFavorite={favoriteIds.includes(option.id)} onToggle={() => onToggleFavorite(option.id)} />
      </Box>
    </li>
  );
}

function ResultsPaperWithKeyboardHelp({ children, ...props }: React.HTMLAttributes<HTMLElement>) {
  return (
    <Paper {...props}>
      {children}
      <OmnibarKeyboardHelp />
    </Paper>
  );
}

export function Omnibar() {
  const { context = 'none', citizenArea, contextId = '', serviceProviderUnitId, opticId } = useOmnibarContext();
  const [inputValue, setInputValue] = React.useState<string>('');
  const [contextFilter, setContextFilter] = React.useState<OmnibarContextFilter>('all');
  const [dynamicLinksResourceId, setDynamicLinksResourceId] = React.useState<string>('');
  const [dynamicLinksResourceType, setDynamicLinksResourceType] = React.useState<string>('');
  const [modalControl, setModalControl] = React.useState<JSX.Element>();
  const [adminLinks, setAdminLinks] = React.useState<IAdminMenuLink[]>([]);
  const [hasBeenOpened, setHasBeenOpened] = React.useState(false);

  const [selectedValue] = React.useState<OmnibarEntry | null>(null);
  const [favoriteIds, setFavoriteIds] = React.useState<string[]>(getFavoriteIds);
  const location = useLocation();
  const { pathname } = location;
  const history = useHistory();
  const inputRef = React.useRef<HTMLInputElement>();
  const { isFeatureEnabled } = useFeatureToggles();
  const allowedActions = useAllowedActions();
  const { isTenantAddonEnabled } = useTenantAddons();
  const { showOmnibar, toggleOmnibar, serviceProviderCitizen, authorityCitizen } = useOmnibar();
  const { tenantName, crmUrl } = useTenantConfiguration();
  const hasServiceProviderAreaAccess = useServiceProviderAreaAccess();
  const { isServiceProvider, isOnlyServiceProvider, isOnlyMedicalCollaborator } = useRoleThemes();
  const isServiceProviderOmnibarEnabled = isFeatureEnabled(catalogCorrectionsToggle);
  const separatedCitizenArea = isServiceProviderOmnibarEnabled ? citizenArea : undefined;
  const currentServiceProviderCitizen =
    separatedCitizenArea === 'serviceprovider' && serviceProviderCitizen?.citizenId.toLowerCase() === contextId.toLowerCase() ? serviceProviderCitizen : undefined;
  const currentAuthorityCitizen = separatedCitizenArea === 'authority' && authorityCitizen?.citizenId.toLowerCase() === contextId.toLowerCase() ? authorityCitizen : undefined;
  const adminEntries = useMemo(() => adminMenuEntries(adminLinks), [adminLinks]);
  const catalogEntries = isFeatureEnabled(catalogCorrectionsToggle) ? [...applyCatalogCorrections(omnibarEntries), ...adminEntries] : omnibarEntries;
  const allEntries = currentServiceProviderCitizen ? [...catalogEntries, ...currentServiceProviderCitizen.entries] : catalogEntries;
  const isRecentAndFirstOpenEnabled = isFeatureEnabled(catalogCorrectionsToggle);
  const canSearchRecords = !isOnlyServiceProvider && !isOnlyMedicalCollaborator;
  const identifierEntries = isServiceProviderOmnibarEnabled ? recordIdentifierEntries(inputValue.trim()) : [];

  const omnibarDisabled = useMemo(() => {
    if (pathname.match(/\/embedded\//)) return true;
    return false;
  }, [pathname]);

  function closeDynamicLinks() {
    setDynamicLinksResourceId('');
    setDynamicLinksResourceType('');
  }

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const omnibarLink = params.get('omnibar-link');

    if (omnibarLink) {
      let checkCount = 0;
      const maxCheckCount = (5 * 1000) / 200; // check for max 5 seconds, every 200 ms

      const intervalId = setInterval(() => {
        const element = document.getElementById(omnibarLink);

        if (element) {
          clearInterval(intervalId); // stop checking if the element is found
          const rect = element.getBoundingClientRect();
          setTimeout(() => {
            window.scroll({
              top: rect.y,
              left: 0,
              behavior: 'smooth',
            });
          }, 2500);
        } else if (checkCount >= maxCheckCount) {
          clearInterval(intervalId); // stop checking if the maximum check time is reached
        }
        checkCount = +1;
      }, 200); // check every 200 ms
    }
  }, [location]);

  function filterContextOptions(options: OmnibarEntry[]): OmnibarEntry[] {
    return options.filter((option: OmnibarEntry) => entryContextFilter(option, context, separatedCitizenArea) && entryAuthorityCitizenFilter(option, currentAuthorityCitizen));
  }
  function filterAccessOptions(options: OmnibarEntry[]): OmnibarEntry[] {
    return options.filter(
      (option: OmnibarEntry) =>
        entryAccessFilter(option, isTenantAddonEnabled, isFeatureEnabled, allowedActions, hasServiceProviderAreaAccess, isServiceProvider) &&
        (canSearchRecords || !option.requiresRecordSearch)
    );
  }
  function filterAndSortOptions(options: OmnibarEntry[]) {
    return filterAccessOptions(filterContextOptions(options)).sort((optionA: OmnibarEntry, optionB: OmnibarEntry) => entrySort(optionA, optionB, context));
  }

  function close() {
    toggleOmnibar(false);
  }

  useEffect(() => {
    if (!showOmnibar) {
      setInputValue('');
      setContextFilter('all');
      inputRef && inputRef.current && inputRef.current.blur();
    } else {
      setHasBeenOpened(true);
      inputRef && inputRef.current && inputRef.current.focus();
    }
  }, [showOmnibar]);

  function handleChange(event: React.SyntheticEvent, value: OmnibarEntry | null) {
    if (!value) return;
    if (!isRecordIdentifierEntry(value)) {
      updateSelectionCount(value);
      if (isRecentAndFirstOpenEnabled) recordRecentSelection(value);
    }

    const { callbackFn, url, dynamicLinksAction, getModalControl } = value;
    let openInNewWindow = false;
    if (event.nativeEvent instanceof KeyboardEvent && event.nativeEvent.ctrlKey) openInNewWindow = true;
    if (event.nativeEvent instanceof MouseEvent && event.nativeEvent.ctrlKey) openInNewWindow = true;
    if (url) {
      const newUrl = value.context !== 'none' && url.indexOf(':contextId') > -1 ? url.replace(':contextId', contextId) : url;
      if (openInNewWindow) {
        window.open(newUrl);
        close();
      } else {
        history.push(newUrl);
      }
    } else if (callbackFn) {
      callbackFn(contextId, crmUrl || '', tenantName || '', serviceProviderUnitId, opticId);
      close();
    } else if (dynamicLinksAction) {
      const { resourceType, resourceId } = dynamicLinksAction(contextId);
      setDynamicLinksResourceId(resourceId);
      setDynamicLinksResourceType(resourceType);
    } else if (getModalControl) {
      setModalControl(getModalControl(contextId));
      close();
    }
  }

  const { numberOfTopResults, allItemsSorted } = getTopSelectedResults(allEntries);
  const isKeywordsAndActionTypesEnabled = isFeatureEnabled(catalogCorrectionsToggle);
  function filterOptions(filterableOptions: OmnibarEntry[], state: { inputValue: string }) {
    if (isRecentAndFirstOpenEnabled && !state.inputValue) return filterableOptions;
    const matchingIdentifierEntries = filterableOptions.filter(isRecordIdentifierEntry);
    const catalogOptions = filterableOptions.filter(option => !isRecordIdentifierEntry(option));
    return [...matchingIdentifierEntries, ...relevanceFilterOptions(catalogOptions, state, isKeywordsAndActionTypesEnabled)];
  }

  let options: OmnibarEntry[] = [];
  if (isRecentAndFirstOpenEnabled && !inputValue) {
    options = composeStartEntries(filterAndSortOptions(allEntries), undefined, undefined, favoriteIds);
  } else if (inputValue || numberOfTopResults < OmnibarTopResultThreshold) {
    options = filterAndSortOptions(allEntries);
  } else if (numberOfTopResults >= OmnibarTopResultThreshold) {
    options = filterAccessOptions(filterContextOptions(allItemsSorted));
  }
  const currentContextLabel = isServiceProviderOmnibarEnabled ? currentContextFilterLabel(context) : undefined;
  const showAdministrationFilter = adminEntries.length > 0;
  const showContextFilters = !!currentContextLabel || showAdministrationFilter;
  if (showContextFilters) {
    options = options.filter(option => matchesContextFilter(option, contextFilter));
  }
  const visibleIdentifierEntries = filterAccessOptions(identifierEntries).filter(option => !showContextFilters || matchesContextFilter(option, contextFilter));
  options = [...visibleIdentifierEntries, ...options];

  return omnibarDisabled ? null : (
    <>
      <Paper
        elevation={14}
        sx={{
          left: '50%',
          maxWidth: '80%',
          minWidth: 500,
          opacity: showOmnibar ? 1 : 0,
          position: 'fixed',
          top: '30%',
          transform: 'translateX(-50%)',
          zIndex: showOmnibar ? 21000 : -1,
        }}
      >
        {isServiceProviderOmnibarEnabled && (
          <OmnibarActiveContext
            context={context}
            citizenArea={citizenArea}
            citizenName={currentServiceProviderCitizen?.fullName ?? currentAuthorityCitizen?.fullName}
            serviceProviderUnitName={currentServiceProviderCitizen?.serviceProviderUnitName}
            opticLabel={currentAuthorityCitizen?.opticLabel}
          />
        )}
        {showContextFilters && (
          <OmnibarContextFilters currentContextLabel={currentContextLabel} showAdministration={showAdministrationFilter} selected={contextFilter} onSelect={setContextFilter} />
        )}
        <Autocomplete
          autoComplete
          autoHighlight
          handleHomeEndKeys
          disablePortal
          PaperComponent={isKeywordsAndActionTypesEnabled ? ResultsPaperWithKeyboardHelp : undefined}
          blurOnSelect
          inputValue={inputValue}
          onInputChange={(event, newInputValue) => {
            setInputValue(newInputValue);
          }}
          value={selectedValue}
          onChange={handleChange}
          options={options}
          openOnFocus={isRecentAndFirstOpenEnabled || numberOfTopResults >= OmnibarTopResultThreshold}
          groupBy={(option: OmnibarEntry) => option.group || ''}
          onBlur={close}
          getOptionLabel={(option: OmnibarEntry) => option.label}
          filterOptions={filterOptions}
          noOptionsText={
            isServiceProviderOmnibarEnabled ? (
              <OmnibarEmptyResults
                query={inputValue}
                canSearchRecords={canSearchRecords}
                isFiltered={showContextFilters && contextFilter !== 'all'}
                onShowAll={() => setContextFilter('all')}
                onSearchRecords={() => {
                  openRankedSearch(inputValue.trim());
                  close();
                }}
              />
            ) : undefined
          }
          renderOption={
            isKeywordsAndActionTypesEnabled ? (props, option) => renderOptionWithActionType(props, option, favoriteIds, id => setFavoriteIds(toggleFavorite(id))) : renderOption
          }
          renderInput={params => {
            const { InputProps, inputProps, ...restParams } = params;

            return (
              <TextField
                {...restParams}
                InputProps={{
                  ...InputProps,
                  sx: {
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none !important',
                    },
                  },
                }}
                variant="outlined"
                placeholder="Hvor skal vi hen?"
                inputRef={inputRef}
                inputProps={{
                  ...inputProps,
                  tabIndex: -1,
                  'aria-hidden': showOmnibar ? 'false' : 'true',
                  ...(isKeywordsAndActionTypesEnabled && { 'aria-label': 'Omnibar: søg efter funktioner' }),
                }}
              />
            );
          }}
        />{' '}
      </Paper>
      <Backdrop
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backdropFilter: `blur(${showOmnibar ? 5 : 0}px)`,
          backgroundColor: `rgb(54, 70, 94, ${showOmnibar ? 0.12 : 0})`,
          zIndex: showOmnibar ? 20999 : -1,
        }}
        open={showOmnibar}
      />
      {dynamicLinksResourceId && dynamicLinksResourceType && (
        <DynamicLinks resourceType={dynamicLinksResourceType} resourceId={dynamicLinksResourceId} citizenId={dynamicLinksResourceId} onClose={closeDynamicLinks} />
      )}
      {modalControl}
      {isServiceProviderOmnibarEnabled && hasBeenOpened && <OmnibarAdminLinks onChange={setAdminLinks} />}
    </>
  );
}
